import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { InstallPromptProvider } from "@/contexts/InstallPromptContext";
import Landing from "@/pages/Landing";
import AppLayout from "@/components/AppLayout";
import { LocationProvider } from "@/contexts/LocationContext";
import { Toaster } from "sonner";

// Landing se carga siempre (primera pantalla pública, crítica para LCP/SEO).
// El resto se divide en chunks aparte — así ninguna ruta descarga las 33
// páginas restantes solo para mostrar una.
const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Escandallos = lazy(() => import("@/pages/Escandallos"));
const Mermas = lazy(() => import("@/pages/Mermas"));
const CashFlow = lazy(() => import("@/pages/CashFlow"));
const Explotacion = lazy(() => import("@/pages/Explotacion"));
const AIAnalysis = lazy(() => import("@/pages/AIAnalysis"));
const Plantillas = lazy(() => import("@/pages/Plantillas"));
const QrEmpleados = lazy(() => import("@/pages/QrEmpleados"));
const Catalogo = lazy(() => import("@/pages/Catalogo"));
const Proveedores = lazy(() => import("@/pages/Proveedores"));
const InventarioStock = lazy(() => import("@/pages/InventarioStock"));
const Carta = lazy(() => import("@/pages/Carta"));
const ComparadorPrecios = lazy(() => import("@/pages/ComparadorPrecios"));
const InformeCompras = lazy(() => import("@/pages/InformeCompras"));
const InformeCaja = lazy(() => import("@/pages/InformeCaja"));
const InformePersonal = lazy(() => import("@/pages/InformePersonal"));
const Suscripcion = lazy(() => import("@/pages/Suscripcion"));
const SuscripcionExito = lazy(() => import("@/pages/SuscripcionExito"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const PacoFicha = lazy(() => import("@/pages/PacoFicha"));
const Empleados = lazy(() => import("@/pages/Empleados"));
const Nominas = lazy(() => import("@/pages/Nominas"));
const CalculadoraPublica = lazy(() => import("@/pages/CalculadoraPublica"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const ControlSanitario = lazy(() => import("@/pages/ControlSanitario"));
const ModoTrabajador = lazy(() => import("@/pages/ModoTrabajador"));
const Consumos = lazy(() => import("@/pages/Consumos"));
const Equipos = lazy(() => import("@/pages/Equipos"));
const Ayuda = lazy(() => import("@/pages/Ayuda"));
const Privacidad = lazy(() => import("@/pages/Privacidad"));
const Terminos = lazy(() => import("@/pages/Terminos"));
const Cookies = lazy(() => import("@/pages/Cookies"));
const Servicios = lazy(() => import("@/pages/Servicios"));
const Perfil = lazy(() => import("@/pages/Perfil"));
const ConectaTuCaja = lazy(() => import("@/pages/integraciones/ConectaTuCaja"));
const DescargarApp = lazy(() => import("@/pages/DescargarApp"));
const DescargaPublica = lazy(() => import("@/pages/DescargaPublica"));

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <FullPageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // Red de seguridad. Desde que dueño y trabajador tienen cookies distintas esto
  // ya no debería dispararse nunca: una petición fuera de /t no manda la cabecera
  // de modo trabajador, así que /auth/me jamás devuelve una sesión de captura aquí.
  if (user.capture_only) return <Navigate to="/t" replace />;
  return children;
}

function FullPageLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-cream" data-testid="loading-screen">
      <div className="flex flex-col items-center gap-3">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-edge border-t-brand-terracotta" />
        <p className="label-eyebrow">Cargando</p>
      </div>
    </div>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/calculadora" element={<CalculadoraPublica />} />
        <Route path="/descargar-app" element={<DescargaPublica />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/login" element={<Login />} />
        <Route path="/registro" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/privacidad" element={<Privacidad />} />
        <Route path="/terminos" element={<Terminos />} />
        <Route path="/cookies" element={<Cookies />} />
        <Route path="/t" element={<ModoTrabajador />} />
        <Route
          path="/app"
          element={
            <Protected>
              <LocationProvider>
                <AppLayout />
              </LocationProvider>
            </Protected>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="escandallos" element={<Escandallos />} />
          <Route path="mermas" element={<Mermas />} />
          <Route path="flujo-caja" element={<CashFlow />} />
          <Route path="explotacion" element={<Explotacion />} />
          <Route path="analisis-ia" element={<AIAnalysis />} />
          <Route path="empleados" element={<Empleados />} />
          <Route path="nominas" element={<Nominas />} />
          <Route path="sanidad" element={<ControlSanitario />} />
          <Route path="consumos" element={<Consumos />} />
          <Route path="equipos" element={<Equipos />} />
          <Route path="plantillas" element={<Plantillas />} />
          <Route path="qr-empleados" element={<QrEmpleados />} />
          <Route path="catalogo" element={<Catalogo />} />
          <Route path="proveedores" element={<Proveedores />} />
          <Route path="inventario" element={<InventarioStock />} />
          <Route path="carta" element={<Carta />} />
          <Route path="comparador-precios" element={<ComparadorPrecios />} />
          <Route path="informe-compras" element={<InformeCompras />} />
          <Route path="informe-caja" element={<InformeCaja />} />
          <Route path="informe-personal" element={<InformePersonal />} />
          <Route path="suscripcion" element={<Suscripcion />} />
          <Route path="suscripcion/exito" element={<SuscripcionExito />} />
          <Route path="ayuda" element={<Ayuda />} />
          <Route path="servicios" element={<Servicios />} />
          <Route path="perfil" element={<Perfil />} />
          <Route path="conecta-tu-caja" element={<ConectaTuCaja />} />
          <Route path="admin" element={<AdminPanel />} />
          <Route path="admin/paco/:userId" element={<PacoFicha />} />
        </Route>
        <Route
          path="/app/descargar-app"
          element={
            <Protected>
              <DescargarApp />
            </Protected>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem storageKey="tah_theme">
      <BrowserRouter>
        <InstallPromptProvider>
          <AuthProvider>
            <Toaster
              position="top-center"
              toastOptions={{
                classNames: {
                  success: "toast-brand-success",
                  error: "toast-brand-error",
                  warning: "toast-brand-warning",
                },
              }}
            />
            <AppRouter />
          </AuthProvider>
        </InstallPromptProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
